// Copyright (c) Bentley Systems
import * as React from "react";

type IconProps = {
  name: string;
  style?: React.CSSProperties | undefined;
};

type SvgModule = {
  ReactComponent: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

type SvgImporter = () => Promise<SvgModule>;

const colorfulIconImporters: Record<string, SvgImporter> = {
  "alarm-critical-colorful": async () => import("@itwin/itwinui-icons-color/icons/alarm-critical.svg"),
  "alarm-early-warning-colorful": async () => import("@itwin/itwinui-icons-color/icons/alarm-early-warning.svg"),
  "alarm-priority-1-colorful": async () => import("@itwin/itwinui-icons-color/icons/alarm-priority-1.svg"),
  "alarm-priority-2-colorful": async () => import("@itwin/itwinui-icons-color/icons/alarm-priority-2.svg"),
  "alarm-priority-3-colorful": async () => import("@itwin/itwinui-icons-color/icons/alarm-priority-3.svg"),
  "alarm-priority-4-colorful": async () => import("@itwin/itwinui-icons-color/icons/alarm-priority-4.svg"),
  "alarm-warning-colorful": async () => import("@itwin/itwinui-icons-color/icons/alarm-warning.svg"),
  "alarm-colorful": async () => import("@itwin/itwinui-icons-color/icons/alarm.svg"),
  "bentley-systems-colorful": async () => import("@itwin/itwinui-icons-color/icons/bentley-systems.svg"),
  "chat-new-colorful": async () => import("@itwin/itwinui-icons-color/icons/chat-new.svg"),
  "filetype-amim-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-amim.svg"),
  "filetype-audio-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-audio.svg"),
  "filetype-autocad-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-autocad.svg"),
  "filetype-compressed-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-compressed.svg"),
  "filetype-creative-cloud-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-creative-cloud.svg"),
  "filetype-dgndb-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-dgndb.svg"),
  "filetype-document-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-document.svg"),
  "filetype-dwg-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-dwg.svg"),
  "filetype-image-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-image.svg"),
  "filetype-imodel-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-imodel.svg"),
  "filetype-microstation-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-microstation.svg"),
  "filetype-mism-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-mism.svg"),
  "filetype-msg-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-msg.svg"),
  "filetype-pdf-markup-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-pdf-markup.svg"),
  "filetype-pdf-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-pdf.svg"),
  "filetype-ppt-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-ppt.svg"),
  "filetype-project-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-project.svg"),
  "filetype-raster-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-raster.svg"),
  "filetype-revit-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-revit.svg"),
  "filetype-rfa-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-rfa.svg"),
  "filetype-sketchup-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-sketchup.svg"),
  "filetype-system-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-system.svg"),
  "filetype-txt-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-txt.svg"),
  "filetype-txt-2-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-txt_2.svg"),
  "filetype-unknown-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-unknown.svg"),
  "filetype-vector-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-vector.svg"),
  "filetype-video-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-video.svg"),
  "filetype-visio-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-visio.svg"),
  "filetype-word-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-word.svg"),
  "filetype-xls-colorful": async () => import("@itwin/itwinui-icons-color/icons/filetype-xls.svg"),
  "folder-colorful": async () => import("@itwin/itwinui-icons-color/icons/folder.svg"),
  "forms-shared-help-colorful": async () => import("@itwin/itwinui-icons-color/icons/forms-shared-help.svg"),
  "forms-shared-success-colorful": async () => import("@itwin/itwinui-icons-color/icons/forms-shared-success.svg"),
  "forms-shared-colorful": async () => import("@itwin/itwinui-icons-color/icons/forms-shared.svg"),
  "forms-unshared-help-colorful": async () => import("@itwin/itwinui-icons-color/icons/forms-unshared-help.svg"),
  "forms-unshared-success-colorful": async () => import("@itwin/itwinui-icons-color/icons/forms-unshared-success.svg"),
  "help-hollow-colorful": async () => import("@itwin/itwinui-icons-color/icons/help-hollow.svg"),
  "help-colorful": async () => import("@itwin/itwinui-icons-color/icons/help.svg"),
  "imodel-colorful": async () => import("@itwin/itwinui-icons-color/icons/imodel.svg"),
  "imodeljs-colorful": async () => import("@itwin/itwinui-icons-color/icons/imodeljs.svg"),
  "info-hollow-colorful": async () => import("@itwin/itwinui-icons-color/icons/info-hollow.svg"),
  "project-colorful": async () => import("@itwin/itwinui-icons-color/icons/project.svg"),
  "pw-project-colorful": async () => import("@itwin/itwinui-icons-color/icons/pw-project.svg"),
  "pw-workarea-colorful": async () => import("@itwin/itwinui-icons-color/icons/pw-workarea.svg"),
  "status-draft-colorful": async () => import("@itwin/itwinui-icons-color/icons/status-draft.svg"),
  "status-error-hollow-colorful": async () => import("@itwin/itwinui-icons-color/icons/status-error-hollow.svg"),
  "status-error-colorful": async () => import("@itwin/itwinui-icons-color/icons/status-error.svg"),
  "status-pass-hollow-colorful": async () => import("@itwin/itwinui-icons-color/icons/status-pass-hollow.svg"),
  "status-pass-colorful": async () => import("@itwin/itwinui-icons-color/icons/status-pass.svg"),
  "status-pending-hollow-colorful": async () => import("@itwin/itwinui-icons-color/icons/status-pending-hollow.svg"),
  "status-pending-colorful": async () => import("@itwin/itwinui-icons-color/icons/status-pending.svg"),
  "status-rejected-hollow-colorful": async () => import("@itwin/itwinui-icons-color/icons/status-rejected-hollow.svg"),
  "status-rejected-colorful": async () => import("@itwin/itwinui-icons-color/icons/status-rejected.svg"),
  "status-running-colorful": async () => import("@itwin/itwinui-icons-color/icons/status-running.svg"),
  "status-success-hollow-colorful": async () => import("@itwin/itwinui-icons-color/icons/status-success-hollow.svg"),
  "status-success-colorful": async () => import("@itwin/itwinui-icons-color/icons/status-success.svg"),
  "status-update-colorful": async () => import("@itwin/itwinui-icons-color/icons/status-update.svg"),
  "status-warning-colorful": async () => import("@itwin/itwinui-icons-color/icons/status-warning.svg"),
  "technical-preview-badge-colorful": async () => import("@itwin/itwinui-icons-color/icons/technical-preview-badge.svg"),
  "technical-preview-mini-colorful": async () => import("@itwin/itwinui-icons-color/icons/technical-preview-mini.svg"),
  "technical-preview-colorful": async () => import("@itwin/itwinui-icons-color/icons/technical-preview.svg"),
};

const lazyIconCache = new Map<string, React.LazyExoticComponent<React.ComponentType<React.SVGProps<SVGSVGElement>>>>();

const getLazyColorfulIcon = (
  name: string
): React.LazyExoticComponent<React.ComponentType<React.SVGProps<SVGSVGElement>>> | undefined => {
  if (lazyIconCache.has(name)) {
    return lazyIconCache.get(name);
  }

  const importer = colorfulIconImporters[name];
  if (!importer) {
    return undefined;
  }

  const LazyIcon = React.lazy(async () => {
    const module = await importer();
    return { default: module.ReactComponent };
  });

  lazyIconCache.set(name, LazyIcon);
  return LazyIcon;
};

export const isColorfulIconName = (name: string): boolean => {
  return colorfulIconImporters[name.toLowerCase()] !== undefined;
};

const ColorfulIconByName = (props: IconProps) => {
  const normalizedName = props.name.toLowerCase();
  const LazyIcon = React.useMemo(
    () => getLazyColorfulIcon(normalizedName),
    [normalizedName]
  );

  if (!LazyIcon) {
    return null;
  }

  return (
    <React.Suspense fallback={null}>
      <LazyIcon style={props.style} />
    </React.Suspense>
  );
};

export default ColorfulIconByName;

